import { useNavigate } from "react-router-dom";
import { LayoutModal } from "../../../../general/modules/commons/layoutModal/layoutModal";
import { useRouteSettingsHook } from "../../infrastructure/hooks/useRouteSettingsHook";
import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { EntitySelectField } from "../../../../general/fields/modules/commons/entitySelectField/entitySelectField";
import { driverSchema } from "../../../truck/domain/schema/truckFormSchema";
import { RouteTruckLayout } from "./RouteTruckLayout";
import { useMemo } from "react";
import { TruckLicencePlateSelectField } from "../../../truck/modules/commons/TruckLicencePlateSelectField";
import { useAppSelector } from "../../../../../hooks/useTypedSelector";
import LicensePlate from "license-plate";
import { TruckLayoutExportButton } from "./TruckLayoutExportButton";
import { PackingSlipExportButton } from "./PackingSlipExportButton";

export const RouteSettingsModal = () => {
  const navigate = useNavigate();

  const { t } = useTranslation("common");

  const {
    truck,
    route,
    removeRoute,
    setDriverId,
    userFilters,
    isValidRoute,
    isValidRouteForLayout,
    updateRouteTruck,
    turnOnGeneration,
    updateRouteCustom,
  } = useRouteSettingsHook();

  const driverValue = useMemo(() => {
    if (route?.driver) {
      return route.driver;
    }

    if (truck?.driver) {
      return truck?.driver;
    }

    return null;
  }, [route?.driver, truck?.driver]);

  const { trucks } = useAppSelector((state) => state.planning);

  if (!route) {
    return null;
  }

  const title =
    new LicensePlate(truck?.licence ?? "").pretty() + " - " + truck?.title;

  return (
    <LayoutModal
      title={title ?? ""}
      show={true}
      onHide={() => {
        navigate(-1);
      }}
      actions={
        <>
          {isValidRoute && (
            <Button
              className={"btn-light"}
              onClick={() => {
                removeRoute(route);
              }}
            >
              {t("Delete route")}
            </Button>
          )}
        </>
      }
    >
      <div className={"d-flex justify-content-end"}>
        <div className={"ms-2"}>
          <PackingSlipExportButton route={route} />
        </div>
        <div className={"ms-2"}>
          <TruckLayoutExportButton route={route} />
        </div>
      </div>
      <Form.Group className="mb-3">
        <Form.Label>{t("Driver")}</Form.Label>
        <EntitySelectField
          filters={userFilters.filters}
          prefix="users"
          entity="User"
          labelPrefix={"name"}
          id={"users.id"}
          key={"users.id"}
          name={"users.id"}
          value={driverValue?.id}
          onChange={(option: unknown) => {
            const typedOption = option as {
              value: string;
              label: string;
            } | null;
            if (typedOption === null) return;
            if (typedOption.value === driverValue?.id) return;
            setDriverId(typedOption);
          }}
          schema={driverSchema}
          limit={1000000}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>{t("Truck")}</Form.Label>
        <TruckLicencePlateSelectField
          value={truck?.id ?? null}
          isClearable={false}
          onChange={(option: unknown) => {
            const selected = Array.isArray(option) ? option[0] : option;
            const selectedTruck = trucks.find(
              (t) => t.id === (selected as { value: string }).value,
            );
            if (selectedTruck) {
              updateRouteTruck(selectedTruck);
            }
          }}
          isInvalid={false}
          isMulti={false}
        />
      </Form.Group>

      {isValidRouteForLayout && (
        <>
          <div className={"d-flex justify-content-between mb-3 mt-5"}>
            <h5>{t("Truck layout")}</h5>
            <div className={"me-4"}>
              {!route.custom ? (
                <span className={"badge bg-success fw-bold"}>
                  {t("This layout was generated successfully")}
                </span>
              ) : (
                <div>
                  <span className={"badge bg-danger fw-bold me-2"}>
                    {t("Custom changed made, the layout will not be generated")}
                  </span>

                  <Button onClick={() => turnOnGeneration()} size="sm">
                    {t("Turn on generation")}
                  </Button>
                </div>
              )}
            </div>
          </div>
          <RouteTruckLayout
            route={route}
            updateRouteCustom={updateRouteCustom}
          />
        </>
      )}
    </LayoutModal>
  );
};
